import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchNotifications,
  fetchNotificationUnreadCount,
  markNotificationsRead,
  markAllNotificationsRead,
  fetchNotificationPreferences,
  saveNotificationPreferences,
  type NotificationListParams,
  type NotificationCategory,
  type NotificationPreferences,
} from '@/lib/api/admin-notifications'

export const notificationKeys = {
  all: ['admin-notifications'] as const,
  list: (params: NotificationListParams) => [...notificationKeys.all, 'list', params] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
}

/**
 * Badge poll. Matches the 30s cadence already used by useAdminFeedbackList; the
 * QueryClient's refetchOnWindowFocus covers the "came back to the tab" case, so
 * there is no Realtime subscription here (admin.notifications is not in the
 * supabase_realtime publication).
 */
export function useNotificationUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: fetchNotificationUnreadCount,
    refetchInterval: 30_000,
  })
}

export function useNotificationList(params: NotificationListParams, enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => fetchNotifications(params),
    enabled,
  })
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => markNotificationsRead(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to mark as read'),
  })
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (category?: NotificationCategory | null) => markAllNotificationsRead(category),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to mark all as read'),
  })
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: fetchNotificationPreferences,
    select: (data) => data.preferences,
    staleTime: 5 * 60 * 1000,
  })
}

export function useSaveNotificationPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (preferences: NotificationPreferences) => saveNotificationPreferences(preferences),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all })
      toast.success('Notification preferences saved')
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to save preferences'),
  })
}
